import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RefreshableLink } from "./RefreshableLink";
import { CgProfile } from "react-icons/cg";
import { FiShoppingCart } from "react-icons/fi";
import { RiSettings3Line, RiStore2Line } from "react-icons/ri";
import { tokenExists, handleLogout, getMe } from "../api/auth";
import InlineLoading from "./InlineLoading";
import { getBranches } from '../api';
import { hasAnyPermission, hasPermission } from "../utils/permissions";
import { NAV_SECTIONS } from "../config/navConfig";
import { normalizePaginatedResponse } from "../utils/pagination";
import {
  requestOpenPosNavbarCart,
  subscribePosNavbarCartState,
} from "../utils/posNavbarCart";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const match = location.pathname.match(/\/branches\/(\d+)/);
  const branchId = match ? parseInt(match[1]) : localStorage.getItem("selectedBranchId");

  const [user, setUser] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [branchMenuOpen, setBranchMenuOpen] = useState(false);
  const [branchModalOpen, setBranchModalOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);
  const [branchError, setBranchError] = useState("");
  const profileMenuRef = useRef();
  const branchMenuRef = useRef();
  const branchMenuMobileRef = useRef();
  const navMenuRef = useRef();
  const [openSection, setOpenSection] = useState(null);
  const [openMobileSection, setOpenMobileSection] = useState(null);
  const [posCartState, setPosCartState] = useState({ visible: false, totalAmount: 0 });

  const toggleSidebar = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (!next) {
        setOpenMobileSection(null);
      }
      return next;
    });
  };
  const toggleProfileMenu = () => {
    setProfileMenuOpen((prev) => !prev);
    setBranchMenuOpen(false);
  };
  const toggleBranchMenu = () => {
    setBranchMenuOpen((prev) => !prev);
    setProfileMenuOpen(false);
    setOpenSection(null);
  };
  const toggleSection = (sectionId) => {
    setOpenSection((prev) => (prev === sectionId ? null : sectionId));
  };
  const toggleMobileSection = (sectionId) => {
    setOpenMobileSection((prev) => (prev === sectionId ? null : sectionId));
  };
  useEffect(() => {
    const fetchUser = async () => {
      if (!tokenExists()) return;
      try {
        const data = await getMe();
        setUser(data);
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    fetchUser();
  }, []);

  const navSections = useMemo(() => {
    if (!tokenExists()) return [];
    const sorted = [...NAV_SECTIONS].sort((a, b) => (a.order || 0) - (b.order || 0));
    return sorted
      .map((section) => {
        const items = (section.items || [])
          .map((item) => {
            const resolvedPath =
              typeof item.path === "function" ? item.path({ branchId }) : item.path;
            if (item.requiresBranch && !branchId) return null;
            if (!resolvedPath) return null;
            if (item.permissions && item.permissions.length) {
              const allowed = item.requireAll
                ? item.permissions.every((permission) => hasPermission(user, permission))
                : hasAnyPermission(user, item.permissions);
              if (!allowed) return null;
            }
            return { ...item, path: resolvedPath };
          })
          .filter(Boolean);
        if (!items.length) return null;
        const isSingle = items.length === 1;
        return {
          ...section,
          items,
          isSingle,
          directPath: isSingle ? items[0].path : null,
        };
      })
      .filter(Boolean);
  }, [user, branchId]);

  const publicRoutes = [{ path: "/login", label: "Login" }];
  const canReadBranches = hasPermission(user, "branch.read");
  const canAccessSettings = hasPermission(user, "branch.access.any");
  const isPosPage = useMemo(
    () =>
      /^\/branches\/\d+\/(purchases\/new|purchases\/\d+\/edit|quotations\/new|quotations\/\d+\/edit)(\/|$)/.test(
        location.pathname
      ),
    [location.pathname]
  );
  const shouldShowPosCart = tokenExists() && isPosPage && posCartState.visible;
  const formattedPosCartTotal = Number(posCartState.totalAmount || 0).toLocaleString("de-DE");
  const userDisplayName = String(user?.name || "").trim();
  const userDisplayEmail = String(user?.email || user?.username || "").trim();
  const currentBranchId = branchId ? String(branchId) : "";
  const currentBranch = useMemo(
    () => branches.find((branch) => String(branch.id) === currentBranchId),
    [branches, currentBranchId]
  );
  const currentBranchLabel = currentBranch?.branchName
    || (currentBranchId ? `Sucursal ${currentBranchId}` : "Sin sucursal seleccionada");
  const canChangeBranch = canReadBranches && (!branches.length || branches.length > 1 || !currentBranchId);
  const hasSingleBranch = branches.length === 1;

  const fetchBranches = useCallback(async () => {
    if (!tokenExists() || !canReadBranches) return;
    setBranchLoading(true);
    setBranchError("");
    try {
      const data = await getBranches({ pageSize: 0 });
      const normalized = normalizePaginatedResponse(data);
      setBranches(normalized.data || []);
    } catch (err) {
      console.error("Error fetching branches:", err);
      setBranchError("No se pudieron cargar las sucursales.");
    } finally {
      setBranchLoading(false);
    }
  }, [canReadBranches]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
      const clickedInsideBranchMenu = Boolean(
        (branchMenuRef.current && branchMenuRef.current.contains(e.target))
        || (branchMenuMobileRef.current && branchMenuMobileRef.current.contains(e.target))
      );
      if (!clickedInsideBranchMenu) {
        setBranchMenuOpen(false);
      }
      if (navMenuRef.current && !navMenuRef.current.contains(e.target)) {
        setOpenSection(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setOpenSection(null);
    setBranchMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const unsubscribe = subscribePosNavbarCartState((event) => {
      const detail = event?.detail || {};
      const nextTotal = Number(detail.totalAmount);
      setPosCartState({
        visible: Boolean(detail.visible),
        totalAmount: Number.isFinite(nextTotal) ? nextTotal : 0,
      });
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!isPosPage) {
      setPosCartState({ visible: false, totalAmount: 0 });
    }
  }, [isPosPage]);

  useEffect(() => {
    if (!branchMenuOpen && !branchModalOpen) return;
    if (branches.length || branchLoading) return;
    fetchBranches();
  }, [branchMenuOpen, branchModalOpen, branches.length, branchLoading, fetchBranches]);

  const handleOpenBranchModal = () => {
    if (!canChangeBranch) return;
    setBranchModalOpen(true);
    setBranchMenuOpen(false);
    setProfileMenuOpen(false);
    if (!branches.length && !branchLoading) {
      fetchBranches();
    }
  };

  const handleSelectBranch = (nextBranchId) => {
    const nextId = String(nextBranchId);
    if (!nextId) return;
    localStorage.setItem("selectedBranchId", nextId);
    setBranchModalOpen(false);
    setBranchMenuOpen(false);
    if (match) {
      const nextPath = location.pathname.replace(/\/branches\/\d+/, `/branches/${nextId}`);
      navigate(`${nextPath}${location.search}`, { replace: true });
    }
  };

  const handleNavigate = (path) => {
    if (!path) return;
    setIsOpen(false);
    setOpenSection(null);
    setOpenMobileSection(null);
    setBranchMenuOpen(false);
    setProfileMenuOpen(false);
    if (location.pathname === path) {
      window.location.href = path;
      return;
    }
    navigate(path);
  };

  const handleOpenSettings = () => {
    setBranchMenuOpen(false);
    setProfileMenuOpen(false);
    setOpenSection(null);
    navigate("/settings");
  };

  const handleOpenPosCart = () => {
    requestOpenPosNavbarCart();
  };

  return (
    <div className="relative">
      <nav className="fixed top-0 left-0 w-full z-30 bg-gray-800/90 backdrop-blur-md text-white px-4 py-2 shadow-md flex items-center justify-between md:justify-between">
        {/* Hamburger Menu (Mobile Only) */}
        <div className="md:hidden flex items-center">
          <button onClick={toggleSidebar}>
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
            </svg>
          </button>
        </div>

        {/* Centered Logo */}
        <div className="flex-1 flex justify-center md:justify-start">
          <img
            src="/logo.png"
            alt="Logo"
            className="h-10 cursor-pointer"
            onClick={() => {
              const canEnterPos = hasAnyPermission(user, ["pos.sell", "pos.open", "pos.close"]);
              const targetPath = canEnterPos && branchId
                ? `/branches/${branchId}/purchases/new`
                : "/";
              if (location.pathname === targetPath) {
                window.location.reload();
              } else {
                navigate(targetPath);
              }
            }}
          />
        </div>

        {/* Right Mobile Icon (Only on Mobile) */}
        {tokenExists() && (
          <div className="md:hidden flex items-center gap-3">
            {shouldShowPosCart && (
              <button
                type="button"
                className="text-white hover:text-gray-300 p-0.5 flex flex-col items-center leading-none"
                onClick={handleOpenPosCart}
                aria-label="Abrir carrito"
              >
                <FiShoppingCart size={22} />
                <span className="text-[11px] font-semibold text-blue-200 leading-none">
                  ${formattedPosCartTotal}
                </span>
              </button>
            )}
            {canAccessSettings && (
              <button
                type="button"
                className="text-white hover:text-gray-300"
                onClick={handleOpenSettings}
              >
                <RiSettings3Line size={24} />
              </button>
            )}
            <div className="relative" ref={branchMenuMobileRef}>
              <button
                type="button"
                className="text-white hover:text-gray-300"
                onClick={toggleBranchMenu}
              >
                <RiStore2Line size={24} />
              </button>
              {branchMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 rounded-lg bg-gray-900/95 text-gray-100 border border-gray-700 shadow-lg z-30">
                  <div className="px-4 py-3 border-b border-gray-700">
                    <div className="text-xs uppercase text-gray-400">Sucursal actual</div>
                    <div className="text-sm font-medium text-gray-100">{currentBranchLabel}</div>
                    {!canReadBranches && (
                      <div className="text-xs text-gray-400 mt-2">
                        No tienes permisos para ver sucursales.
                      </div>
                    )}
                    {branchError && (
                      <div className="text-xs text-red-300 mt-2">{branchError}</div>
                    )}
                    {branchLoading && (
                      <div className="text-xs text-gray-400 mt-2">
                        <InlineLoading label="Cargando sucursales" />
                      </div>
                    )}
                    {!branchLoading && !branchError && canReadBranches && branches.length === 0 && (
                      <div className="text-xs text-gray-400 mt-2">No hay sucursales disponibles.</div>
                    )}
                  </div>
                  <div className="px-4 py-3">
                    <button
                      type="button"
                      onClick={handleOpenBranchModal}
                      disabled={!canChangeBranch}
                      className="w-full text-left px-3 py-2 rounded bg-gray-800 text-gray-100 hover:bg-gray-700 disabled:opacity-50"
                    >
                      {currentBranchId ? "Cambiar sucursal" : "Seleccionar sucursal"}
                    </button>
                    {hasSingleBranch && currentBranchId && (
                      <div className="text-xs text-gray-400 mt-2">
                        Solo tienes acceso a una sucursal.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Desktop Navigation */}
        <ul className="hidden md:flex items-center space-x-4" ref={navMenuRef}>
          {tokenExists() ? (
            navSections.map((section) => (
              <li key={section.id} className="relative">
                {section.isSingle ? (
                  <button
                    type="button"
                    onClick={() => handleNavigate(section.directPath)}
                    className="flex items-center hover:text-gray-300"
                  >
                    <span>{section.label}</span>
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleSection(section.id)}
                      className="flex items-center gap-2 hover:text-gray-300"
                      aria-expanded={openSection === section.id}
                    >
                      <span>{section.label}</span>
                      <span className="text-xs">▾</span>
                    </button>
                    {openSection === section.id && (
                      <div className="absolute left-0 mt-2 w-60 rounded-lg bg-gray-900/95 text-gray-100 border border-gray-700 shadow-lg overflow-hidden">
                        <div className="py-1">
                          {section.items.map((item) => {
                            const isActive = location.pathname === item.path;
                            return (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => {
                                  setOpenSection(null);
                                  handleNavigate(item.path);
                                }}
                                className={`w-full text-left px-4 py-2 text-sm transition ${
                                  isActive
                                    ? "bg-blue-500/20 text-blue-200"
                                    : "text-gray-100 hover:bg-gray-800/80 hover:text-white"
                                }`}
                              >
                                {item.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </li>
            ))
          ) : (
            publicRoutes.map((route) => (
              <li key={route.path}>
                <RefreshableLink to={route.path} label={route.label} />
              </li>
            ))
          )}

          {shouldShowPosCart && (
            <li>
              <button
                type="button"
                className="text-white hover:text-gray-300 p-0.5 flex flex-col items-center leading-none"
                onClick={handleOpenPosCart}
                aria-label="Abrir carrito"
              >
                <FiShoppingCart size={22} />
                <span className="text-[11px] font-semibold text-blue-200 leading-none">
                  ${formattedPosCartTotal}
                </span>
              </button>
            </li>
          )}

          {tokenExists() && (
            <li className="relative" ref={branchMenuRef}>
              <button
                type="button"
                onClick={toggleBranchMenu}
                className="text-white hover:text-gray-300"
              >
                <RiStore2Line size={24} />
              </button>
              {branchMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 rounded-lg bg-gray-900/95 text-gray-100 border border-gray-700 shadow-lg z-30">
                  <div className="px-4 py-3 border-b border-gray-700">
                    <div className="text-xs uppercase text-gray-400">Sucursal actual</div>
                    <div className="text-sm font-medium text-gray-100">{currentBranchLabel}</div>
                    {!canReadBranches && (
                      <div className="text-xs text-gray-400 mt-2">
                        No tienes permisos para ver sucursales.
                      </div>
                    )}
                    {branchError && (
                      <div className="text-xs text-red-300 mt-2">{branchError}</div>
                    )}
                    {branchLoading && (
                      <div className="text-xs text-gray-400 mt-2">
                        <InlineLoading label="Cargando sucursales" />
                      </div>
                    )}
                    {!branchLoading && !branchError && canReadBranches && branches.length === 0 && (
                      <div className="text-xs text-gray-400 mt-2">No hay sucursales disponibles.</div>
                    )}
                  </div>
                  <div className="px-4 py-3">
                    <button
                      type="button"
                      onClick={handleOpenBranchModal}
                      disabled={!canChangeBranch}
                      className="w-full text-left px-3 py-2 rounded bg-gray-800 text-gray-100 hover:bg-gray-700 disabled:opacity-50"
                    >
                      {currentBranchId ? "Cambiar sucursal" : "Seleccionar sucursal"}
                    </button>
                    {hasSingleBranch && currentBranchId && (
                      <div className="text-xs text-gray-400 mt-2">
                        Solo tienes acceso a una sucursal.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </li>
          )}

          {tokenExists() && canAccessSettings && (
            <li>
              <button
                type="button"
                onClick={handleOpenSettings}
                className="text-white hover:text-gray-300"
              >
                <RiSettings3Line size={24} />
              </button>
            </li>
          )}

          {tokenExists() && (
            <li className="relative" ref={profileMenuRef}>
              <button onClick={toggleProfileMenu} className="text-white hover:text-gray-300">
                <CgProfile size={26} />
              </button>
              {profileMenuOpen && (
                <div className="absolute right-0 mt-2 w-52 rounded-lg bg-gray-900/95 text-gray-100 border border-gray-700 shadow-lg">
                  {(userDisplayName || userDisplayEmail) && (
                    <div className="px-4 py-3 border-b border-gray-700">
                      <p className="text-xs uppercase text-gray-400">Sesión activa</p>
                      <p className="text-sm font-medium text-gray-100 truncate">
                        {userDisplayName || userDisplayEmail}
                      </p>
                      {userDisplayName && userDisplayEmail && userDisplayEmail.toLowerCase() !== userDisplayName.toLowerCase() && (
                        <p className="text-xs text-gray-400 truncate">{userDisplayEmail}</p>
                      )}
                    </div>
                  )}
                  <button
                    onClick={() => navigate("/change-password")}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-800/80 hover:text-white"
                  >
                    Mi Perfil
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-300 hover:bg-red-500/10 hover:text-red-200"
                  >
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </li>
          )}
        </ul>
      </nav>

      {/* Sidebar (Mobile) */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-30" onClick={toggleSidebar}>
          <div className="fixed top-0 left-0 h-full w-64 bg-gray-800 text-white z-40" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-4 right-4" onClick={toggleSidebar}>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <ul className="p-6 space-y-4 mt-12">
              {tokenExists() ? (
                navSections.map((section) => (
                  <li key={section.id}>
                    {section.isSingle ? (
                      <button
                        type="button"
                        onClick={() => handleNavigate(section.directPath)}
                        className="w-full text-left px-2 py-2 rounded text-gray-100 hover:bg-gray-700/60 hover:text-white"
                      >
                        {section.label}
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => toggleMobileSection(section.id)}
                          className="w-full flex items-center justify-between text-left px-2 py-2 rounded text-gray-100 hover:bg-gray-700/60 hover:text-white"
                        >
                          <span>{section.label}</span>
                          <span className="text-xs">
                            {openMobileSection === section.id ? "▲" : "▼"}
                          </span>
                        </button>
                        {openMobileSection === section.id && (
                          <ul className="mt-2 space-y-2 pl-1">
                            {section.items.map((item) => {
                              const isActive = location.pathname === item.path;
                              return (
                                <li key={item.id}>
                                  <button
                                    type="button"
                                    onClick={() => handleNavigate(item.path)}
                                    className={`w-full text-left px-3 py-2 rounded text-sm ${
                                      isActive
                                        ? "bg-blue-500/20 text-blue-200"
                                        : "text-gray-200 hover:bg-gray-700/60 hover:text-white"
                                    }`}
                                  >
                                    {item.label}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </>
                    )}
                  </li>
                ))
              ) : (
                publicRoutes.map((route) => (
                  <li key={route.path}>
                    <button
                      type="button"
                      onClick={() => handleNavigate(route.path)}
                      className="w-full text-left px-2 py-2 rounded text-gray-100 hover:bg-gray-700/60 hover:text-white"
                    >
                      {route.label}
                    </button>
                  </li>
                ))
              )}
              {tokenExists() && (
                <>
                  <li>
                    <button
                      onClick={() => navigate("/change-password")}
                      className="block text-left w-full px-2 py-2 rounded text-gray-100 hover:bg-gray-700/60 hover:text-white"
                    >
                      Mi Perfil
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={handleLogout}
                      className="block text-left w-full px-2 py-2 rounded text-red-300 hover:bg-red-500/10 hover:text-red-200"
                    >
                      Cerrar Sesión
                    </button>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      )}

      {branchModalOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50 px-4"
          onClick={() => setBranchModalOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-lg w-full max-w-md p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold">Seleccionar sucursal</h3>
                <p className="text-xs text-gray-500">Elige la sucursal para trabajar.</p>
              </div>
              <button
                type="button"
                onClick={() => setBranchModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                x
              </button>
            </div>

            {!canReadBranches && (
              <div className="mt-4 text-sm text-gray-600">
                No tienes permisos para ver sucursales.
              </div>
            )}
            {branchError && (
              <div className="mt-4 text-sm text-red-600">{branchError}</div>
            )}
            {branchLoading && (
              <div className="mt-4 text-sm text-gray-500">
                <InlineLoading label="Cargando sucursales" />
              </div>
            )}
            {!branchLoading && !branchError && canReadBranches && branches.length === 0 && (
              <div className="mt-4 text-sm text-gray-500">No hay sucursales disponibles.</div>
            )}

            {!branchLoading && !branchError && canReadBranches && branches.length > 0 && (
              <div className="mt-4 space-y-2 max-h-72 overflow-y-auto">
                {branches.map((branch) => {
                  const isSelected = currentBranchId && String(branch.id) === currentBranchId;
                  return (
                    <button
                      key={branch.id}
                      type="button"
                      onClick={() => handleSelectBranch(branch.id)}
                      disabled={isSelected}
                      className={`w-full text-left px-3 py-2 rounded border ${
                        isSelected
                          ? "border-blue-400 bg-blue-50 text-blue-700"
                          : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                      }`}
                    >
                      <div className="text-sm font-medium">{branch.branchName}</div>
                      <div className="text-xs text-gray-500">
                        Sucursal {branch.id}{isSelected ? " (actual)" : ""}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setBranchModalOpen(false)}
                className="px-4 py-2 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Navbar;
